import {
  EventCalendar,
  EventCalendarTicketEntry,
  EventReservedTickets,
} from 'app/pages/TicketSalePage/slice/types';
import { DateTime } from 'luxon';

export const dateFormatForCalendar = 'yyyy-LL-dd';

export const getClosestAvailableDay = (
  calendar: EventCalendar,
  reservedTickets: EventReservedTickets,
  timezone: string,
): DateTime | undefined => {
  const today = DateTime.now().setLocale('en-US').setZone(timezone);
  const todayDayOfWeek = today.weekday;
  const todayDateStr = today.toFormat(dateFormatForCalendar);
  const maxSearchDays = 365;

  // check if "everyday" tickets are available today
  if (
    getAvailableTickets(calendar, reservedTickets, 'everyday', todayDateStr)
      .length > 0
  )
    return today;

  // check if today has tickets based on a specific date
  if (
    getAvailableTickets(calendar, reservedTickets, todayDateStr, todayDateStr)
      .length > 0
  )
    return today;

  // check if today has tickets based on the day of the week
  if (
    getAvailableTickets(
      calendar,
      reservedTickets,
      todayDayOfWeek.toString(),
      todayDateStr,
    ).length > 0
  )
    return today;

  // find the closest available day
  for (let i = 1; i <= maxSearchDays; i++) {
    const checkDate = today.plus({ days: i });
    const checkDayOfWeek = checkDate.weekday;
    const checkDateStr = checkDate.toFormat(dateFormatForCalendar);

    if (
      getAvailableTickets(calendar, reservedTickets, checkDateStr, checkDateStr)
        .length > 0
    ) {
      return checkDate;
    }

    if (
      getAvailableTickets(
        calendar,
        reservedTickets,
        checkDayOfWeek.toString(),
        checkDateStr,
      ).length > 0
    ) {
      return checkDate;
    }

    if (
      getAvailableTickets(calendar, reservedTickets, 'everyday', checkDateStr)
        .length > 0
    ) {
      return checkDate;
    }
  }
};

export interface CalendarWeekData {
  [key: string]: EventCalendarTicketEntry[];
}

export const getTicketsForSpecificDate = (
  calendar: EventCalendar,
  date: DateTime,
): EventCalendarTicketEntry[] => {
  const result: EventCalendarTicketEntry[] = [];
  const dayOfWeek = date.weekday;
  const dateStr = date.toFormat(dateFormatForCalendar);

  let availableTickets = getTickets(calendar, 'everyday', dateStr);
  if (availableTickets.length > 0) {
    result.push(...availableTickets);
  }

  availableTickets = getTickets(calendar, dateStr, dateStr);
  if (availableTickets.length > 0) {
    result.push(...availableTickets);
  }

  availableTickets = getTickets(calendar, dayOfWeek.toString(), dateStr);
  if (availableTickets.length > 0) {
    result.push(...availableTickets);
  }

  return result;
};

const checkIfDateIsBetweenInterval = (currentDate, startDate, endDate) => {
  if (!startDate && !endDate) {
    return true;
  }
  if (!startDate || !endDate) {
    if (!startDate && endDate && currentDate <= endDate) {
      return true;
    }
    if (startDate && currentDate >= startDate && !endDate) {
      return true;
    }
    return false;
  }
  if (startDate <= currentDate && currentDate <= endDate) {
    return true;
  }
  return false;
};

// returns the available tickets on a given day
const getAvailableTickets = (
  calendar: EventCalendar,
  reservedTickets: EventReservedTickets,
  day: string,
  targetDate: string,
) => {
  const tickets = calendar[day];

  if (!tickets) return [];

  return tickets.filter(
    ({ ticketTypeId, limit, startDate, endDate }) =>
      checkIfTicketTypeIsOrderableOnASpecificDate(
        reservedTickets,
        ticketTypeId,
        limit,
        day,
      ) && checkIfDateIsBetweenInterval(targetDate, startDate, endDate),
  );
};

export const checkIfTicketTypeIsOrderableOnASpecificDate = (
  reservedTickets: EventReservedTickets,
  ticketTypeId: string,
  limit: number | null,
  day: string | DateTime,
) => {
  if (!limit) return true;

  const dayStr =
    typeof day === 'string' ? day : day.toFormat(dateFormatForCalendar);
  const reservedQuantity = reservedTickets[ticketTypeId]?.[dayStr];

  if (reservedQuantity === undefined) return true;

  return reservedQuantity < limit;
};

// returns the tickets on a given day
const getTickets = (
  calendar: EventCalendar,
  day: string,
  targetDate: string,
) => {
  const tickets = calendar[day];

  if (!tickets) return [];

  return tickets.filter(({ startDate, endDate }) =>
    checkIfDateIsBetweenInterval(targetDate, startDate, endDate),
  );
};

export type CalendarDayStatus =
  | 'available'
  | 'filling-up-fast'
  | 'sold-out'
  | 'unavailable';

export const getDayStatusOnCalendar = (
  calendar: EventCalendar,
  reservedTickets: EventReservedTickets,
  day: DateTime,
): CalendarDayStatus => {
  const tickets = getTicketsForSpecificDate(calendar as EventCalendar, day);
  const formattedDate = day.toFormat(dateFormatForCalendar);

  if (tickets.length === 0) return 'unavailable';
  if (
    tickets.every(
      ({ ticketTypeId, limit }) =>
        !checkIfTicketTypeIsOrderableOnASpecificDate(
          reservedTickets,
          ticketTypeId,
          limit,
          formattedDate,
        ),
    )
  )
    return 'sold-out';
  if (
    tickets.find(({ ticketTypeId, limit }) => {
      if (limit === null) return false;

      const reservedQuantity = reservedTickets[ticketTypeId]?.[formattedDate];

      if (!reservedQuantity) return false;

      const percentageOfSoldTickets = (reservedQuantity * 100) / limit;

      return percentageOfSoldTickets >= 80;
    })
  )
    return 'filling-up-fast';

  return 'available';
};

export const dayColorsBasedOnStatus: Record<CalendarDayStatus, string> = {
  available: 'green',
  'filling-up-fast': 'orange',
  'sold-out': 'red',
  unavailable: 'neutral',
};

export const getTimetableForTicket = (
  calendar: EventCalendar,
  date: DateTime,
  id: string,
): EventCalendarTicketEntry => {
  const formattedDate = date.toFormat(dateFormatForCalendar);

  if (calendar.hasOwnProperty(formattedDate)) {
    const entry = calendar[formattedDate].find(
      ({ ticketTypeId }) => ticketTypeId === id,
    );

    if (entry) return entry;
  }

  if (calendar.hasOwnProperty('everyday')) {
    const entry = calendar['everyday'].find(
      ({ ticketTypeId }) => ticketTypeId === id,
    );

    if (entry) return entry;
  }

  if (calendar.hasOwnProperty(date.weekday)) {
    const entry = calendar[date.weekday].find(
      ({ ticketTypeId }) => ticketTypeId === id,
    );

    if (entry) return entry;
  }

  throw new Error("Ticket doesn't have an associated timetable");
};

// export const ticketHasCalendar = (id: string, calendar: EventCalendar) => {
//   for (const day in calendar) {
//     if (calendar[day].find(({ ticketTypeId }) => ticketTypeId === id)) {
//       return true;
//     }
//   }
//   return false;
// };

// export const getNonCalendarTickets = (
//   tickets: Ticket[],
//   calendar: EventCalendar,
// ) => tickets.filter(ticket => !ticketHasCalendar(ticket.id, calendar));
